import React, { useState, ChangeEvent, Fragment, useRef, useCallback, useEffect } from "react";
import register from "../../registerServiceWorker";
import { getInputClassName } from "../../utils/utils";
import { ISelectProps } from "../types/Select.interface";

const SelectInput: React.FC<ISelectProps> = (props) => {
    const [touched, setTouch] = useState(props.touched);
    const [error, setError] = useState("");
    const [htmlClass, setHtmlClass] = useState("");
    const [value, setValue] = useState(props.value);
    const [className, setClassName] = useState(getInputClassName(touched, props.inputClass, htmlClass));

    // Used for referencing the input fields
    const selectInputRef = useRef<HTMLSelectElement>();

    const setFocus = useCallback((): void => {
        selectInputRef.current?.focus();
    }, []);

    useEffect(() => {
        // Autofocus the element.
        if (props.autofocus) {
            setTimeout(setFocus, 500);
        }
    }, [props.autofocus, setFocus]);

    const resetSelectedItem = useCallback(() => {
        if (props.value.length === 0 && selectInputRef && selectInputRef.current) {
            // Reset the options so nothing is selected.
            selectInputRef.current.selectedIndex = 0;

            // This doesn't seem right but this was causing a bug that after deselecting the value would be 
            // selected again because the state 'value' was not updated with the updated props.value!
            if (value !== props.value) {
                setValue(props.value);
            }
        }
    }, [props.value, value]);

    useEffect(() => {
        if (!props.touched) {
            setHtmlClass("");
            setClassName(getInputClassName(props.touched, props.inputClass, ""));
            resetSelectedItem();
        }
    }, [props.touched, props.inputClass, resetSelectedItem]);

    function onValueChanged(event: ChangeEvent<HTMLSelectElement>): void {
        let [error, validClass, elementValue] = ["", "", event.target.value];

        [error, validClass] = ((!elementValue || elementValue.length === 0 || elementValue.trim().length === 0) && props.required) ?
            ["Value has to be selected", "is-invalid"] : ["", "is-valid"];

        props.onChange({ value: elementValue, error: error, touched: true, field: props.field });

        setTouch(true);
        setError(error);
        setHtmlClass(validClass);
        setValue(elementValue);
        setClassName(getInputClassName(true, props.inputClass, validClass));
    }

    /**
     * Creates the option elements to display in the drop down list.
     **/
    const getOptions: (JSX.Element | null)[] = props.options.map(option => {
        return (
            <option key={option} value={`${option}`}>{option}</option>
        )
    });

    return (
        <Fragment>
            <label htmlFor={`${props.id}`}>{props.label}</label>
            <select
                value={value}
                id={`${props.id}`}
                className={className}
                onChange={onValueChanged}
                ref={(e: HTMLSelectElement) => { register(); selectInputRef.current = e; }}
            >
                <option value="">Choose...</option>
                {getOptions}
            </select>

            {error ?
                <div className="invalid-feedback">
                    {error}
                </div> : null
            }
        </Fragment>
    );
}

export default SelectInput;